import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import React, { useEffect, useState, useContext, useRef } from 'react';
import { doc, getDoc, onSnapshot, updateDoc, arrayUnion, serverTimestamp, writeBatch } from 'firebase/firestore';
import { auth, db, functions } from '../firebase';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { Link } from 'react-router-dom';
import { safeCommentPhotoUrls } from '../utils/photoUrl';
import { useCommentCooldown } from '../hooks/useCommentCooldown';
import { cooldownMessage, isOfferComment } from '../utils/commentCooldown';
import { MembershipContext } from '../contexts/MembershipContext';
import { UserContext } from '../contexts/UserContext';
import { formatCurrencyInput } from '../utils/currencyUtils';
import { useContact } from '../contexts/ContactContext';
import { useNotifications } from '../contexts/NotificationContext';
import imageCompression from 'browser-image-compression';
import AddressRequiredModal from '../components/AddressRequiredModal';
import { Capacitor } from '@capacitor/core';
import { beginOperation, markQueued, markConfirmed, markFailed, getPendingRecord, clearRecord, resolveAgainstOffer } from '../utils/offerResponseLedger';
import SellingDisabledBanner from '../components/SellingDisabledBanner';
import PhotoGatePanel, { isPhotoGateClosed } from '../components/PhotoGatePanel';
import { useSellingDisabled } from '../hooks/useSellingDisabled';
import { validateMessage } from '../utils/profanityFilter';
import AutoGrowTextarea from '../components/AutoGrowTextarea';
import { formatRelativeTime } from '../utils/relativeTime';
import { useReceivedOfferPushPrompt } from '../hooks/useReceivedOfferPushPrompt';
import { isOfferAwaitingMyResponse } from '../utils/offerAwaitingResponse';
import AdminCancelledBanner from '../components/AdminCancelledBanner';
import { placeholderFor, SKETCH_PLACEHOLDER_URL } from '../utils/listingPlaceholder';
import { isAutoDeclined, autoDeclineReasonText } from '../utils/autoDeclineCopy';

const MAX_COMMENT_IMAGES = 3;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const COMPRESSION_THRESHOLD_MB = 1.5;

// One-tap decline reasons. Declining has to be faster than ignoring, because an
// ignored offer expires silently at 48h and tells the buyer nothing.
// "Looking for more" is not a decline at all — it routes into the counter flow.
const DECLINE_REASONS = [
  { value: 'holding', label: "Thanks, but I'm holding onto this one" },
  { value: 'not_selling', label: 'Not selling right now' },
  { value: 'prefer_trade', label: "I'd rather trade than sell" },
  { value: 'wants_more', label: "I'm looking for a bit more", routesToCounter: true }
];

const PurchaseOfferDetails = () => {
  const { offerId } = useParams();
  const { openContact } = useContact();
  const { notifications, markAsRead, loading: notificationsLoading } = useNotifications();
  const { disabled: sellingDisabled } = useSellingDisabled();
  const [offerData, setOfferData] = useState(null);
  const [targetPedal, setTargetPedal] = useState(null);
  const [responseType, setResponseType] = useState('');
  const [counterAmount, setCounterAmount] = useState('');
  const [note, setNote] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  // Comment-box errors shown under the box; see OfferPage for why.
  const [commentError, setCommentError] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialOfferMessage, setInitialOfferMessage] = useState('');
  const [buyerUserData, setBuyerUserData] = useState({ username: '', badge: '', membershipTier: '', photoURL: '', averageRating: 0 });
  const [sellerUserData, setSellerUserData] = useState({ username: '', badge: '', membershipTier: '', photoURL: '', averageRating: 0 });
  const [currentUserId, setCurrentUserId] = useState(null);
  const [cancellingOffer, setCancellingOffer] = useState(false);
  const cancelOffer = async () => {
    if (!window.confirm('Cancel this offer? The other member will no longer be able to accept it.')) return;
    setCancellingOffer(true);
    try {
      // One status write, no callable; see OfferPage.cancelOffer for why.
      await updateDoc(doc(db, 'purchaseOffers', offerId), {
        status: 'cancelled',
        cancelledBy: currentUserId,
        updatedAt: serverTimestamp(),
        offerUpdatedAt: serverTimestamp(),
        history: arrayUnion({
          type: 'response',
          from: currentUserId,
          responseType: 'cancel',
          message: 'Offer cancelled',
          timestamp: new Date(),
        }),
      });
      navigate('/trade-center');
    } catch (e) {
      console.error('❌ Cancel offer failed:', e);
      alert('Could not cancel this offer. Please try again.');
      setCancellingOffer(false);
    }
  };
  // Submission truthfulness + prior-response verdict banner. Mirrors the
  // trade OfferPage (2026-09-25 lost-decline incident); see the state notes
  // there and utils/offerResponseLedger.js.
  const [submitPhase, setSubmitPhase] = useState('idle'); // idle|submitting|syncing|submitted|failed
  const [priorResponseNotice, setPriorResponseNotice] = useState(null);
  const [offerNotFound, setOfferNotFound] = useState(false);
  const [notLoggedIn, setNotLoggedIn] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [sendingNote, setSendingNote] = useState(false);
  // Four comments per member per conversation in a rolling minute (owner
  // decision, 2026-11-01): counts the notes already in the stored history,
  // so a failed send never counts, and is re-checked inside handleSendNote.
  // The ref stops a same-tick double tap from sending twice.
  const commentCooldown = useCommentCooldown(offerData?.history, currentUserId, isOfferComment);
  const sendNoteInFlightRef = useRef(false);
  const [pendingImageFiles, setPendingImageFiles] = useState([]);
  const [pendingPreviews, setPendingPreviews] = useState([]);
  const fileInputRef = useRef(null);
  const [salesTaxAmount, setSalesTaxAmount] = useState(0);
  const [salesTaxRate, setSalesTaxRate] = useState(0);
  const [salesTaxState, setSalesTaxState] = useState(null);
  const [calculatingSalesTax, setCalculatingSalesTax] = useState(false);

  const [showAddressModal, setShowAddressModal] = useState(false);

  const { firestoreUser, loading: userDataLoading } = useContext(UserContext);

  const isAddressComplete = (address) => {
    if (!address) return false;
    const required = ['name', 'street1', 'city', 'state', 'zip', 'country'];
    return required.every(field => address[field] && address[field].trim());
  };

  const { membershipTier, getTradeCompletionFee, getCashFeePercentage } = useContext(MembershipContext);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromHistory = searchParams.get('from') === 'history';

  const getBadge = (count) => {
    if (count === undefined || count === null || isNaN(count)) return '0';
    return count.toString();
  };

const getMembershipTierText = (tier) => {
  switch (tier) {
    case 'premium_basic': return '9V';
    case 'premium_plus': return '18V';
    default: return 'Free';
  }
};

const getMembershipProfileBadge = (membershipTier) => {
  switch (membershipTier) {
    case 'premium_basic':
      return (
        <div
          style={{
            position: 'absolute',
            top: '-2px',
            left: '-2px',
            backgroundColor: '#2563eb',
            color: 'white',
            fontWeight: 'bold',
            borderRadius: '50%',
            width: '12px',
            height: '12px',
            fontSize: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #fff',
            boxShadow: '0 0 2px rgba(0,0,0,0.3)',
          }}
        >
          9V
        </div>
      );
    case 'premium_plus':
      return (
        <div
          style={{
            position: 'absolute',
            top: '-2px',
            left: '-2px',
            backgroundColor: '#7c3aed',
            color: 'white',
            fontWeight: 'bold',
            borderRadius: '50%',
            width: '12px',
            height: '12px',
            fontSize: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #fff',
            boxShadow: '0 0 2px rgba(0,0,0,0.3)',
          }}
        >
          18V
        </div>
      );
    case 'premium_pro':
      return (
        <div
          style={{
            position: 'absolute',
            top: '-2px',
            left: '-2px',
            backgroundColor: '#d97706',
            color: 'white',
            fontWeight: 'bold',
            borderRadius: '50%',
            width: '12px',
            height: '12px',
            fontSize: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #fff',
            boxShadow: '0 0 2px rgba(0,0,0,0.3)',
          }}
        >
          24V
        </div>
      );
    default:
      return null;
  }
};

  // Mark THIS purchase offer's notifications as read whenever they appear. Scoped
  // to the offer on screen (n.offerId === offerId). Runs on every notifications
  // update (no one-shot guard) so it also clears notifications that load in late
  // — e.g. a cold start from a push tap, where the notification can arrive after
  // the first non-loading render — and when navigating between offers without a
  // remount (the component instance persists).
  useEffect(() => {
    if (notificationsLoading) return;

    const relatedNotifications = notifications.filter(
      n => (n.type === 'new_purchase_offer' || n.type === 'purchase_offer_status_change') &&
           n.offerId === offerId &&
           !n.read
    );

    if (relatedNotifications.length > 0) {
      relatedNotifications.forEach(n => markAsRead(n.id));
    }
  }, [notifications, markAsRead, offerId, notificationsLoading]);

  // Auto-open the inline address modal when the user lands here without a
  // complete address. Same trigger as the original "Shipping Address Required"
  // modal — the difference is that the modal now contains the address form
  // itself instead of redirecting to /profile.
  useEffect(() => {
    if (!userDataLoading && !isAddressComplete(firestoreUser?.shippingAddress)) {
      setShowAddressModal(true);
    } else {
      setShowAddressModal(false);
    }
  }, [firestoreUser, userDataLoading]);

  const calculateSalesTaxForBuyer = async (purchaseAmount, cashFee) => {
    if (!firestoreUser?.shippingAddress) {
      setSalesTaxAmount(0);
      setSalesTaxRate(0);
      setSalesTaxState(null);
      return;
    }

    setCalculatingSalesTax(true);
    try {
      const normalizedAddress = {
        ...firestoreUser.shippingAddress,
        state: firestoreUser.shippingAddress.state === 'Colorado' ? 'CO' : firestoreUser.shippingAddress.state
      };

      const calculateSalesTaxFn = httpsCallable(functions, 'calculateSalesTax');
      const result = await calculateSalesTaxFn({
        shippingAddress: normalizedAddress,
        purchaseAmount: purchaseAmount,
        platformFee: cashFee
      });

      if (result.data && result.data.enabled) {
        setSalesTaxAmount(result.data.taxAmount || 0);
        setSalesTaxRate(result.data.taxRatePercent || 0);
        setSalesTaxState(result.data.state || null);
      } else {
        setSalesTaxAmount(0);
        setSalesTaxRate(0);
        setSalesTaxState(null);
      }
    } catch (error) {
      console.error('Error calculating sales tax:', error);
    } finally {
      setCalculatingSalesTax(false);
    }
  };

  useEffect(() => {
    const buyerViewing = currentUserId && offerData && currentUserId === offerData.initiatorId;
    if (!buyerViewing || !responseType) {
      setSalesTaxAmount(0);
      setSalesTaxRate(0);
      setSalesTaxState(null);
      return;
    }

    const offerAmount = responseType === 'accept'
      ? (offerData?.counterAmount || offerData?.offerAmount || 0)
      : Number(counterAmount || 0);

    if (offerAmount <= 0) {
      setSalesTaxAmount(0);
      setSalesTaxRate(0);
      setSalesTaxState(null);
      return;
    }

    const cashFeePercDecimal = getCashFeePercentage() / 100;
    const cashFee = Number((offerAmount * cashFeePercDecimal).toFixed(2));

    const debounceTimer = setTimeout(() => {
      calculateSalesTaxForBuyer(offerAmount, cashFee);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [responseType, counterAmount, currentUserId, offerData, firestoreUser]);

  useEffect(() => {
    // LIVE subscription, not a one-shot getDoc. Two reasons, both found during
    // Android testing (2026-08-20):
    //   1. A push tap targeting the route the user is ALREADY on is a router
    //      no-op — no remount, no refetch — so the page showed the original
    //      offer after a counter arrived.
    //   2. A counter landing while the user is viewing now updates in place.
    // The viewedAt marker below is loop-safe: our own write re-fires the
    // snapshot, but by then myViewedMs >= stateMs so it skips.
    //
    // Storm dedupe (2026-09-25): Jason's device trace on the sibling
    // OfferPage caught the iOS listen storm — the SAME doc delivered every
    // ~8ms. This callback has no generation guard, so a storm here wouldn't
    // hang, but it would fire three getDocs per delivery (~375 reads/sec)
    // and thrash state. Identical server versions are skipped up front.
    let lastSnapVersion = null;
    let unsubOffer = null;
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) { setNotLoggedIn(true); return; }
      setCurrentUserId(user.uid);

      const offerRef = doc(db, 'purchaseOffers', offerId);
      if (unsubOffer) unsubOffer();
      unsubOffer = onSnapshot(offerRef, async (offerSnap) => {
      const snapVersion = offerSnap.updateTime ? offerSnap.updateTime.toMillis() : null;
      if (snapVersion !== null && lastSnapVersion === snapVersion && !offerSnap.metadata.hasPendingWrites) {
        return;
      }
      lastSnapVersion = snapVersion;
      if (!offerSnap.exists()) {
        setOfferNotFound(true);
        return;
      }

      const data = offerSnap.data();
      setOfferData({ id: offerSnap.id, ...data });

      // Mark this purchase offer as viewed by the current user, but only if:
      //   (a) the user is the active recipient of the current offer state, and
      //   (b) their existing viewedAt entry is missing or stale relative to offerUpdatedAt.
      // This write is idempotent — refreshes skip it. See firestore.rules
      // isViewedAtSelfOnly() for the matching server-side guard.
      // Recipient = whoever did NOT make the last offer/response action.
      // Status alone isn't sufficient because counter-counters keep the status
      // as 'countered' while flipping the direction.
      const actionEntries = (data.history || []).filter(e => e.type === 'offer' || e.type === 'response');
      const lastActionFrom = actionEntries.length > 0 ? actionEntries[actionEntries.length - 1].from : null;
      const isActiveStatus = data.status === 'pending' || data.status === 'countered';
      const currentRecipientUid = (isActiveStatus && lastActionFrom)
        ? (lastActionFrom === data.initiatorId ? data.responderId : data.initiatorId)
        : null;
      if (currentRecipientUid === user.uid && data.offerUpdatedAt) {
        const stateMs = data.offerUpdatedAt.toMillis?.() ?? 0;
        const myViewedMs = data.viewedAt?.[user.uid]?.toMillis?.() ?? 0;
        if (stateMs > 0 && myViewedMs < stateMs) {
          // Fire-and-forget: don't block subsequent page loads on this write.
          updateDoc(offerRef, { [`viewedAt.${user.uid}`]: serverTimestamp() })
            .catch(err => console.warn('Failed to mark purchase offer as viewed:', err));
        }
      }

      const initialMessage = data.history?.find((entry) => entry.type === 'offer')?.message || 'No offer message found.';
      setInitialOfferMessage(initialMessage);

      const targetPedalSnap = await getDoc(doc(db, 'listings', data.targetPedalId));
      let targetPedalData = null;
      if (targetPedalSnap.exists()) {
        targetPedalData = { id: targetPedalSnap.id, ...targetPedalSnap.data() };
      } else if (data.pedalSnapshot?.targetPedal) {
        targetPedalData = { id: data.targetPedalId, ...data.pedalSnapshot.targetPedal };
      }

      if (!targetPedalData) {
        console.error('❌ Target pedal listing does not exist and no snapshot is available.');
        return;
      }

      setTargetPedal(targetPedalData);

      // PII isolation: read sanitized public mirror.
      const buyerUserSnap = await getDoc(doc(db, 'publicProfiles', data.initiatorId));
      const sellerUserSnap = await getDoc(doc(db, 'publicProfiles', data.responderId));

      const buyerUser = buyerUserSnap.exists() ? buyerUserSnap.data() : {};
      const sellerUser = sellerUserSnap.exists() ? sellerUserSnap.data() : {};

      const buyerCompleted = buyerUser.completedTrades || 0;
      const sellerCompleted = sellerUser.completedTrades || 0;

      setBuyerUserData({
        username: buyerUser.username || 'Unknown',
        badge: getBadge(buyerCompleted),
        membershipTier: buyerUser.membershipTier || '',
        photoURL: buyerUser.photoURL || '/images/default-avatar.png',
        averageRating: buyerUser.averageRating || 0
      });

      setSellerUserData({
        username: sellerUser.username || 'Unknown',
        badge: getBadge(sellerCompleted),
        membershipTier: sellerUser.membershipTier || '',
        photoURL: sellerUser.photoURL || '/images/default-avatar.png',
        averageRating: sellerUser.averageRating || 0
      });
      }, (err) => {
        console.error('Purchase offer snapshot error:', err);
      });
    });

    return () => {
      if (unsubOffer) unsubOffer();
      unsubscribe();
    };
  }, [offerId]);

  const handleCommentFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    const remaining = MAX_COMMENT_IMAGES - pendingImageFiles.length;
    if (remaining <= 0) {
      setCommentError(`Maximum ${MAX_COMMENT_IMAGES} images per comment.`);
      return;
    }

    for (const file of files.slice(0, remaining)) {
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        setCommentError('Please use JPEG, PNG, or WebP images only.');
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setCommentError('Each image must be under 10MB.');
        return;
      }
    }

    const toAdd = files.slice(0, remaining);
    const newPreviews = toAdd.map((f) => URL.createObjectURL(f));
    setPendingImageFiles((prev) => [...prev, ...toAdd]);
    setPendingPreviews((prev) => [...prev, ...newPreviews]);
    setCommentError('');
  };

  const removeCommentImage = (index) => {
    setPendingImageFiles((prev) => prev.filter((_, i) => i !== index));
    setPendingPreviews((prev) => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSendNote = async () => {
    if (sendNoteInFlightRef.current) return;
    const hasText = noteText.trim().length > 0;
    const hasImages = pendingImageFiles.length > 0;
    if (!hasText && !hasImages) return;
    const gate = commentCooldown.check();
    if (!gate.allowed) {
      // Draft text and pending photos are left untouched.
      setCommentError(cooldownMessage(gate.remainingMs));
      return;
    }

    if (hasText) {
      const validation = validateMessage(noteText);
      if (!validation.isValid) {
        setCommentError(validation.error);
        return;
      }
    }

    sendNoteInFlightRef.current = true;
      setSendingNote(true);
    try {
      const user = auth.currentUser;
      // Throw, never return, from inside the try: the in-flight guard and
      // the Sending state are reset in the finally below. A bare return here
      // left the composer stuck on "Sending..." when auth was momentarily
      // null (token refresh on app resume); review 2026-11-01.
      if (!user) throw new Error('User not authenticated');

      let imageUrls = [];
      if (pendingImageFiles.length > 0) {
        const storage = getStorage();
        const basePath = `offer_message_photos/${offerId}`;
        const timestamp = Date.now();
        for (let i = 0; i < pendingImageFiles.length; i++) {
          const file = pendingImageFiles[i];
          let fileToUpload = file;
          const originalSizeMB = file.size / 1024 / 1024;
          if (originalSizeMB > COMPRESSION_THRESHOLD_MB) {
            try {
              const compressed = await imageCompression(file, {
                maxSizeMB: 1.5,
                maxWidthOrHeight: 1800,
                useWebWorker: false,
                quality: 0.85,
                fileType: 'image/jpeg',
              });
              fileToUpload = compressed;
            } catch (compressionErr) {
              console.warn('Comment image compression failed, using original:', compressionErr);
            }
          }
          const unique = `${user.uid}_${timestamp}_${i}_${Math.random().toString(36).slice(2, 9)}`;
          const storageRef = ref(storage, `${basePath}/${unique}.jpg`);
          await uploadBytes(storageRef, fileToUpload);
          const url = await getDownloadURL(storageRef);
          imageUrls.push(url);
        }
        pendingPreviews.forEach((url) => URL.revokeObjectURL(url));
        setPendingImageFiles([]);
        setPendingPreviews([]);
      }

      const historyEntry = {
        type: 'note',
        from: user.uid,
        message: noteText.trim().slice(0, 500),
        timestamp: new Date()
      };
      if (imageUrls.length > 0) historyEntry.imageUrls = imageUrls;

      const offerRef = doc(db, 'purchaseOffers', offerId);
      await updateDoc(offerRef, {
        history: arrayUnion(historyEntry),
        updatedAt: serverTimestamp()
      });
      setNoteText('');
      const offerSnap = await getDoc(offerRef);
      if (offerSnap.exists()) {
        setOfferData({ id: offerSnap.id, ...offerSnap.data() });
      }
    } catch (err) {
      console.error('Error sending note:', err);
      setCommentError('Failed to send comment. Please try again.');
    } finally {
      sendNoteInFlightRef.current = false;
      setSendingNote(false);
    }
  };

  // Verdict check for a prior session's unconfirmed response, and the
  // pre-issuance exit warning. Both mirror the trade OfferPage; see the
  // comments there and utils/offerResponseLedger.js for the semantics.
  useEffect(() => {
    if (!offerData || !currentUserId || submitPhase !== 'idle') return;
    const record = getPendingRecord(offerId);
    if (!record) {
      setPriorResponseNotice(null);
      return;
    }
    const verdict = resolveAgainstOffer(record, offerData, currentUserId);
    if (verdict === 'confirmed') {
      markConfirmed(offerId, record.opId);
      setPriorResponseNotice(null);
      return;
    }
    setPriorResponseNotice({ verdict, responseType: record.responseType });
  }, [offerData, currentUserId, offerId, submitPhase]);

  // Native "turn on notifications" pop-up when this purchase offer is
  // waiting on the user (2026-10-07). Uses its own strict check (notes
  // ignored); the page's response buttons keep their existing logic.
  useReceivedOfferPushPrompt(
    offerId,
    !!targetPedal && isOfferAwaitingMyResponse(offerData, currentUserId, {
      routeId: offerId, initiatorField: 'initiatorId', recipientField: 'responderId',
    })
  );

  useEffect(() => {
    if (Capacitor.isNativePlatform() || submitPhase !== 'submitting') return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [submitPhase]);

  const handleResponse = async () => {
    if (!responseType) {
      setErrorMsg('Please select a response option.');
      return;
    }

    if (responseType === 'accept' && !isAddressComplete(firestoreUser?.shippingAddress)) {
      setShowAddressModal(true);
      return;
    }

    let ledgerOpId = null;
    try {
      setLoading(true);
      setErrorMsg('');
      setSubmitPhase('submitting');

      const user = auth.currentUser;
      if (!user) {
        setErrorMsg('You must be logged in to respond to offers.');
        setSubmitPhase('failed');
        setLoading(false); // pre-existing bug: this return left the button stuck on Submitting
        return;
      }

      // Local operation record; see utils/offerResponseLedger.js.
      ledgerOpId = beginOperation({
        offerId,
        responseType,
        preflightUpdatedAt: offerData.updatedAt?.seconds || offerData.updatedAt || 0,
      });

      // Selling-suspension guard. Sellers (responders) cannot accept or
      // counter while suspended. Decline is always allowed.
      const isSellerSide = user.uid === offerData.responderId;
      if (isSellerSide && sellingDisabled && (responseType === 'accept' || responseType === 'counter')) {
        markFailed(offerId, ledgerOpId, 'selling-paused');
        setSubmitPhase('failed');
        setErrorMsg('Selling, trading & listing are currently paused on your account due to an outstanding recovery invoice. You can still decline this offer.');
        setLoading(false); // pre-existing stuck-button bug, same as above
        return;
      }

      let validatedCounterAmount = 0;
      if (responseType === 'counter' && counterAmount) {
        const rawAmount = Number(counterAmount);
        if (isNaN(rawAmount) || rawAmount <= 0) {
          markFailed(offerId, ledgerOpId, 'validation:counter');
          setSubmitPhase('failed');
          setErrorMsg('Please enter a valid counter offer amount (must be positive)');
          setLoading(false); // pre-existing stuck-button bug, same as above
          return;
        }
        if (rawAmount > 2000) {
          markFailed(offerId, ledgerOpId, 'validation:counter');
          setSubmitPhase('failed');
          setErrorMsg('Counter offer amount cannot exceed $2,000');
          setLoading(false); // pre-existing stuck-button bug, same as above
          return;
        }
        validatedCounterAmount = Math.round(rawAmount * 100) / 100;
      }

      const safeNote = note.trim().slice(0, 500);

      if (safeNote) {
        const validation = validateMessage(safeNote);
        if (!validation.isValid) {
          markFailed(offerId, ledgerOpId, 'validation:note');
          setSubmitPhase('failed');
          setErrorMsg(validation.error);
          setLoading(false);
          return;
        }
      }

      let responseMessage = '';
      if (responseType === 'accept') {
        const acceptedAmount = offerData.counterAmount || offerData.offerAmount;
        responseMessage = `Accepted offer of $${acceptedAmount}`;
      } else if (responseType === 'decline') {
        responseMessage = declineReason ? `Passed on this offer: ${declineReason}` : 'Passed on this offer';
      } else if (responseType === 'counter') {
        responseMessage = `Countered with $${validatedCounterAmount}`;
      }

      if (safeNote) {
        responseMessage += ` - ${safeNote}`;
      }

      const offerRef = doc(db, 'purchaseOffers', offerId);
      const updateData = {
        status: responseType === 'accept' ? 'accepted' : responseType === 'decline' ? 'declined' : 'countered',
        updatedAt: serverTimestamp(),
        history: arrayUnion({
          type: 'response',
          from: user.uid,
          message: responseMessage,
          timestamp: new Date(),
          responseType: responseType,
          counterAmount: responseType === 'counter' ? validatedCounterAmount : null
        })
      };

      if (responseType === 'accept') {
        updateData.acceptedAmount = offerData.counterAmount || offerData.offerAmount;
        updateData.shippingStatus = {
          confirmationPhoto: null,
          confirmationPhotoUploadedAt: null,
          labelCreated: false,
          labelPurchased: false,
          labelUrl: null,
          trackingNumber: null,
          delivered: false,
          deliveredAt: null,
          inTransit: false,
          currentTrackingStatus: null,
          lastUpdated: null,
          trackingHistory: []
        };
      } else if (responseType === 'counter') {
        updateData.counterAmount = validatedCounterAmount;
        updateData.offerUpdatedAt = serverTimestamp();
      }

      // THE CRITICAL WRITE. Same contract as the trade OfferPage: nothing
      // after this may hold the confirmation hostage.
      markQueued(offerId, ledgerOpId);
      setSubmitPhase('syncing');
      await updateDoc(offerRef, updateData);
      markConfirmed(offerId, ledgerOpId);
      setSubmitPhase('submitted');

      // Housekeeping runs detached behind the receipt (see the trade
      // OfferPage restructure, 2026-09-25). Each block keeps its own
      // try/catch; the live subscription refreshes page state regardless.
      const runPostResponseTail = async () => {
      if (responseType === 'accept') {
        try {
          let pedalSnapshot = null;
          const targetPedalSnap = await getDoc(doc(db, 'listings', offerData.targetPedalId));
          
          if (targetPedalSnap.exists()) {
            pedalSnapshot = {
              targetPedal: { id: targetPedalSnap.id, ...targetPedalSnap.data() },
              snapshotCreatedAt: new Date().toISOString()
            };
          }
          
          const batch = writeBatch(db);
          
          if (pedalSnapshot) {
            const purchaseDocRef = doc(db, 'purchaseOffers', offerId);
            batch.update(purchaseDocRef, { pedalSnapshot });
          }
          
          const targetPedalRef = doc(db, 'listings', offerData.targetPedalId);
          batch.update(targetPedalRef, {
            inTrade: true,
            purchaseAcceptedAt: serverTimestamp(),
            purchaseOfferId: offerId
          });
          
          await batch.commit();
        } catch (pedalError) {
          console.error('❌ Error marking pedal as in-trade or saving snapshot:', pedalError);
        }
      }

      // Legacy refresh, retained but detached: the live subscription
      // delivers the same updated offer on its own.
      if (responseType !== 'accept') {
        try {
          const refreshRef = doc(db, 'purchaseOffers', offerId);
          const offerSnap = await getDoc(refreshRef);
          if (offerSnap.exists()) {
            const updatedData = offerSnap.data();
            setOfferData({ id: offerSnap.id, ...updatedData });
          }
        } catch (refreshError) {
          console.error('❌ Error refreshing offer data:', refreshError);
        }
      }
      }; // end runPostResponseTail

      if (responseType === 'accept') {
        // Fire the housekeeping and go: the confirm page must not wait on it.
        runPostResponseTail();
        const isSeller = user.uid === offerData.responderId;
        if (isSeller) {
          navigate(`/seller-purchase-confirm/${offerId}`);
        } else {
          navigate(`/purchase-confirm/${offerId}`);
        }
      } else {
        // Receipt first, housekeeping after.
        setResponseType('');
        setCounterAmount('');
        setNote('');
        setDeclineReason('');
        setLoading(false);
        runPostResponseTail();
        alert('Response submitted!');
      }

    } catch (err) {
      console.error('🔥 Detailed error on purchase response:', err);
      if (ledgerOpId) markFailed(offerId, ledgerOpId, err?.code || err?.name || 'unknown');
      setSubmitPhase('failed');
      setErrorMsg('Something went wrong. Please try again.');
    }

    setLoading(false);
  };

  if (offerNotFound) {
    return (
      <div style={{ 
        minHeight: '50vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{
          maxWidth: '500px',
          width: '100%',
          backgroundColor: '#fff3cd',
          border: '2px solid #ffc107',
          borderRadius: '12px',
          padding: '2rem',
          textAlign: 'center',
          fontFamily: 'Nunito, sans-serif'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#856404',
            marginBottom: '1rem'
          }}>
            Offer No Longer Available
          </h2>
          <p style={{
            fontSize: '1rem',
            color: '#856404',
            lineHeight: '1.6',
            marginBottom: '1.5rem'
          }}>
            This purchase offer has been retracted by the other user or is no longer available.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              backgroundColor: '#C9A227',
              color: 'white',
              padding: '0.75rem 2rem',
              border: 'none',
              borderRadius: '6px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              fontFamily: 'Nunito, sans-serif'
            }}
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }
  
  if (notLoggedIn) {
    return (
      <div style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ maxWidth: '500px', width: '100%', backgroundColor: '#e8f4fd', border: '2px solid #5A9ECB', borderRadius: '12px', padding: '2rem', textAlign: 'center', fontFamily: 'Nunito, sans-serif' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1a5276', marginBottom: '1rem' }}>Log In to View This Offer</h2>
          <p style={{ fontSize: '1rem', color: '#2c3e50', lineHeight: '1.6', marginBottom: '1.5rem' }}>You need to be logged in to view purchase offer details.</p>
          <Link to="/login" style={{ display: 'inline-block', backgroundColor: '#5A9ECB', color: 'white', fontWeight: '600', padding: '0.75rem 2rem', border: 'none', borderRadius: '6px', fontSize: '1rem', textDecoration: 'none', fontFamily: 'Nunito, sans-serif' }}>Go to Login</Link>
        </div>
      </div>
    );
  }

  if (!offerData || !targetPedal) return <div className="p-4">Loading offer...</div>;

  const lastHistoryEntry = offerData.history && offerData.history.length > 0
    ? offerData.history[offerData.history.length - 1]
    : null;
  const lastResponderId = lastHistoryEntry ? lastHistoryEntry.from : null;
  
  const isOfferActive = ['pending', 'countered'].includes(offerData.status);

  const canRespond = currentUserId && 
    isOfferActive && 
    (
      !lastResponderId ||
      (lastResponderId && currentUserId !== lastResponderId)
    );
    
  const mustWait = currentUserId && 
    isOfferActive && 
    lastResponderId && 
    currentUserId === lastResponderId;

  const getExpirationTime = (createdAt) => {
    if (!createdAt) return null;
    
    const createdDate = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
    const expirationDate = new Date(createdDate.getTime() + (48 * 60 * 60 * 1000));
    const now = new Date();
    
    const timeLeft = expirationDate.getTime() - now.getTime();
    
    if (timeLeft <= 0) {
      return 'Expired';
    }
    
    const hoursLeft = Math.floor(timeLeft / (1000 * 60 * 60));
    const minutesLeft = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hoursLeft > 24) {
      const daysLeft = Math.floor(hoursLeft / 24);
      return `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`;
    } else if (hoursLeft > 0) {
      return `${hoursLeft}h ${minutesLeft}m left`;
    } else {
      return `${minutesLeft}m left`;
    }
  };

  const expirationTime = getExpirationTime(offerData.createdAt);

  const isBuyer = currentUserId === offerData.initiatorId;
  // The pedal photo taps through to the listing on the same terms as the
  // "View listing" text link (owner, 2026-11-01: on a phone that small link
  // was the only way in). Photo only, so the clickable boundary is the square
  // photo and nothing else; mirrors OfferPedalFields on OfferPage.
  const targetListingTo = targetPedal?.id && !['declined', 'expired', 'cancelled'].includes(offerData.status)
    ? `/listing/${targetPedal.id}?fromOffer=true`
    : null;
  const targetPhoto = targetPedal ? (
    <>
        {targetPedal.images?.top ? (
          <img className="purchase-offer-pedal-img" src={targetPedal.images.top} alt="pedal"
            style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '8px' }} />
        ) : (
          <img className="purchase-offer-pedal-img" src={placeholderFor(targetPedal).url} alt="Photos coming" onError={(e) => { if (!e.currentTarget.src.endsWith(SKETCH_PLACEHOLDER_URL)) e.currentTarget.src = SKETCH_PLACEHOLDER_URL; }}
            style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '8px', opacity: 0.85 }} />
        )}
    </>
  ) : null;
  const myData = isBuyer ? buyerUserData : sellerUserData;
  const theirData = isBuyer ? sellerUserData : buyerUserData;
  const myUserId = isBuyer ? offerData.initiatorId : offerData.responderId;
  const theirUserId = isBuyer ? offerData.responderId : offerData.initiatorId;

  return (
    <>
    <div className="p-4" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))', display: 'flex', flexDirection: 'column' }}>
      {/* Prior-response verdict banner (offerResponseLedger); mirrors the
          trade OfferPage. 'lost' only when the offer provably didn't change. */}
      {priorResponseNotice && submitPhase === 'idle' && (
        <div style={{
          backgroundColor: priorResponseNotice.verdict === 'lost' ? '#fdecea' : '#fff8e6',
          border: `1px solid ${priorResponseNotice.verdict === 'lost' ? '#dc2626' : '#C9A227'}`,
          borderRadius: '10px',
          padding: '0.75rem 1rem',
          margin: '0 0 1rem 0',
          fontFamily: 'Nunito, sans-serif',
          color: '#4A5759',
          fontSize: '0.95rem',
          lineHeight: 1.5
        }}>
          <strong>
            {priorResponseNotice.verdict === 'lost'
              ? 'Your earlier response was not recorded.'
              : 'Your earlier response may not have been recorded because this offer changed.'}
          </strong>{' '}
          {priorResponseNotice.verdict === 'lost'
            ? 'Please review the offer below and submit it again.'
            : 'Please review the latest terms below before responding again.'}
          <button
            onClick={() => { clearRecord(offerId); setPriorResponseNotice(null); }}
            style={{ marginLeft: '0.75rem', background: 'none', border: 'none', color: '#4A9FCA', cursor: 'pointer', fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem', fontWeight: 700, padding: 0 }}
          >
            Dismiss
          </button>
        </div>
      )}
      <style>{`
        @media (min-width: 769px) {
          .purchase-offer-cards-row {
            gap: 1.5rem !important;
          }
          .purchase-offer-pedal-card img.purchase-offer-pedal-img {
            width: 100% !important;
            height: auto !important;
            aspect-ratio: 1;
          }
          .purchase-offer-main-container {
            width: calc(50% + 2rem) !important;
            min-width: calc(800px + 2rem) !important;
          }
          .purchase-offer-lower-sections {
            width: calc(50% + 2rem) !important;
            min-width: calc(800px + 2rem) !important;
          }
          .purchase-offer-note-textarea {
            width: 100% !important;
            box-sizing: border-box !important;
          }
          .history-entry-card {
            max-width: 85% !important;
          }
          .history-entry-mine {
            align-self: flex-end !important;
          }
          .history-entry-theirs {
            align-self: flex-start !important;
          }
        }
        @media (max-width: 768px) {
          .purchase-offer-cards-row {
            gap: 0.75rem !important;
          }
          .purchase-offer-pedal-card {
            flex: 1 1 calc(50% - 0.375rem) !important;
            padding: 0.75rem !important;
          }
          .purchase-offer-pedal-card img.purchase-offer-pedal-img {
            width: 100% !important;
            height: auto !important;
            aspect-ratio: 1;
          }
          .purchase-offer-main-container {
            width: auto !important;
            margin: 0 0.5rem 1rem 0.5rem !important;
            max-width: none !important;
            min-width: auto !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .purchase-offer-lower-sections {
            width: auto !important;
            min-width: auto !important;
            margin: 0 0.5rem !important;
          }
          .purchase-offer-respond-container {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .purchase-offer-response-buttons {
            max-width: none !important;
            width: 100% !important;
          }
          .purchase-offer-note-textarea {
            min-width: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
            margin-left: auto !important;
            margin-right: auto !important;
            display: block !important;
          }
          .purchase-offer-textarea-button-container {
            display: flex !important;
            flex-direction: column !important;
            align-items: stretch !important;
          }
          .purchase-offer-page-footer {
            padding-bottom: 100px !important;
          }
          .comment-subtitle {
            font-size: 0.8rem !important;
          }
          .respond-options-list {
            gap: 5px !important;
          }
          .history-entry-username {
            font-size: 0.78rem !important;
          }
          .history-entry-badge {
            font-size: 0.6rem !important;
            padding: 2px 6px !important;
          }
          .history-entry-date {
            font-size: 0.75rem !important;
          }
          .history-entry-card {
            max-width: 80% !important;
          }
          .history-entry-mine {
            align-self: flex-end !important;
          }
          .history-entry-theirs {
            align-self: flex-start !important;
          }
        }
      `}</style>
      <div style={{ textAlign: 'center', marginTop: '2rem', marginBottom: '1rem' }}>
        {/* Purchase offers live on the Offers page alongside trades (and in
            Offer History), so Back mirrors the trade offer page (OfferPage).
            It used to point at /accepted-purchases, a leftover from before
            offers were unified — no route into this page comes from there. */}
        <Link
          to={fromHistory ? "/offer-history" : "/trade-center"}
          style={{
            color: '#5A9ECB',
            textDecoration: 'none',
            fontSize: '1.125rem'
          }}
        >
          {fromHistory ? '← Back to Offer History' : '← Back to Offers'}
        </Link>
      </div>
      <h2 className="text-2xl font-bold mb-4">Purchase Offer</h2>
      <AdminCancelledBanner offer={offerData} kind="purchase" />
      
      {/* Expiration Time */}
      {offerData.createdAt && (
        <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded" style={{ maxWidth: '600px', margin: '0 auto 1rem auto' }}>
          <p className="text-orange-800 text-sm">
            <strong>⏰ Offer expires:</strong> {expirationTime}
          </p>
        </div>
      )}
      {(() => {
        const actionEntries = (offerData.history || []).filter(e => e.type === 'offer' || e.type === 'response');
        const lastActionFrom = actionEntries.length > 0 ? actionEntries[actionEntries.length - 1].from : null;
        const isActiveStatus = offerData.status === 'pending' || offerData.status === 'countered';
        const recipientUid = (isActiveStatus && lastActionFrom)
          ? (lastActionFrom === offerData.initiatorId ? offerData.responderId : offerData.initiatorId)
          : null;
        if (!recipientUid) return null;
        const stateMs = offerData.offerUpdatedAt?.toMillis?.() ?? 0;
        if (!(stateMs > 0)) return null;
        const viewedMs = offerData.viewedAt?.[recipientUid]?.toMillis?.() ?? 0;
        const isSeen = viewedMs >= stateMs;
        const iAmRecipient = currentUserId === recipientUid;
        // Don't show "Not seen" to the recipient themselves — their viewedAt
        // write is fire-and-forget, so the page would otherwise display "Not
        // seen" for the entire session even though they're literally viewing.
        if (!isSeen && iAmRecipient) return null;
        if (isSeen) {
          const viewedDate = offerData.viewedAt[recipientUid].toDate();
          const viewedStr = viewedDate.toLocaleDateString() + ' · ' + viewedDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
          return (
            <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '1rem', color: '#16a34a', fontWeight: 600 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <span>Offer seen: {viewedStr}</span>
            </div>
          );
        }
        return (
          <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '1rem', color: '#9ca3af', fontWeight: 600 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            <span>Offer not yet seen</span>
          </div>
        );
      })()}

      {/* Pedal Cards Row */}
      <div className="purchase-offer-cards-row purchase-offer-main-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem', marginLeft: 'auto', marginRight: 'auto' }}>

        {/* Buyer Card */}
        <div className="purchase-offer-pedal-card" style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '1rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          flex: '1',
          textAlign: 'left'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <div style={{ position: 'relative', width: '32px', height: '32px', flexShrink: 0 }}>
              <img src={buyerUserData.photoURL || '/images/default-avatar.png'} alt="avatar"
                style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #e5e7eb' }} />
              {getMembershipProfileBadge(buyerUserData.membershipTier)}
            </div>
            <div style={{ textAlign: 'left' }}>
              <button
                onClick={() => navigate(`/profile/${offerData.initiatorId}`)}
                style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1f2937', background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit' }}
                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
              >
                {buyerUserData.username}
              </button>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                <span>{getMembershipTierText(buyerUserData.membershipTier)}</span>
                <span>·</span>
                <span style={{ color: '#f59e0b' }}>★</span>
                <span style={{ fontWeight: '600', color: '#374151' }}>{buyerUserData.averageRating > 0 ? buyerUserData.averageRating.toFixed(1) : '—'}</span>
                {buyerUserData.badge !== null && buyerUserData.badge !== undefined && (
                  <span>({buyerUserData.badge})</span>
                )}
              </div>
            </div>
          </div>

          <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#64748b', letterSpacing: '0.05em', marginBottom: '8px', textTransform: 'uppercase' }}>Buyer</div>

          {(() => {
            // counterAmount is the latest counter from EITHER side, so the raw
            // `counterAmount || offerAmount` here showed the seller's ask under
            // the BUYER's identity after a counter ("I offered $34, why does my
            // chip say $3?" — found during Android testing, 2026-08-20).
            // Attribute honestly: the green chip is always the buyer's own
            // latest number; a pending seller counter gets its own amber line.
            if (offerData.status === 'accepted') {
              return (
                <div style={{ padding: '12px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', letterSpacing: '0.05em', marginBottom: '4px' }}>AGREED PRICE</div>
                  <div style={{ fontWeight: '700', fontSize: '1.25rem', color: '#15803d' }}>
                    ${offerData.acceptedAmount || offerData.counterAmount || offerData.offerAmount}
                  </div>
                </div>
              );
            }
            const actions = (offerData.history || []).filter(e => e.type === 'offer' || e.type === 'response');
            const last = actions.length > 0 ? actions[actions.length - 1] : null;
            const sellerCounterPending = !!(last && last.from === offerData.responderId && last.responseType === 'counter' && offerData.counterAmount);
            // Buyer's own latest number: their most recent history action that
            // carried an amount; the original offerAmount when they've only
            // made the opening offer.
            let buyerAmount = offerData.offerAmount;
            for (let i = actions.length - 1; i >= 0; i--) {
              const e = actions[i];
              if (e.from === offerData.initiatorId && e.counterAmount) { buyerAmount = e.counterAmount; break; }
              if (e.from === offerData.initiatorId && e.type === 'offer') break;
            }
            return (
              <div style={{ padding: '12px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', letterSpacing: '0.05em', marginBottom: '4px' }}>CASH OFFER</div>
                <div style={{ fontWeight: '700', fontSize: '1.25rem', color: '#15803d' }}>
                  ${buyerAmount}
                </div>
                {sellerCounterPending && (
                  <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px dashed #fcd34d' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: '600', color: '#92400e', letterSpacing: '0.05em' }}>SELLER COUNTER</div>
                    <div style={{ fontWeight: '700', fontSize: '1.05rem', color: '#b45309' }}>
                      ${offerData.counterAmount}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Seller / Target Pedal Card */}
        <div className="purchase-offer-pedal-card" style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '1rem',
          border: '2px dashed #C9A227',
          flex: '1',
          textAlign: 'left',
          position: 'relative'
        }}>
          <div style={{
            position: 'absolute',
            top: '-1px',
            right: '12px',
            backgroundColor: '#fef3c7',
            color: '#92400e',
            fontSize: '0.65rem',
            fontWeight: '700',
            padding: '2px 8px',
            borderRadius: '0 0 6px 6px',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            border: '1px solid #C9A227',
            borderTop: 'none'
          }}>
            {isBuyer ? 'THEIR PEDAL' : 'YOUR PEDAL'}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <div style={{ position: 'relative', width: '32px', height: '32px', flexShrink: 0 }}>
              <img src={sellerUserData.photoURL || '/images/default-avatar.png'} alt="avatar"
                style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #e5e7eb' }} />
              {getMembershipProfileBadge(sellerUserData.membershipTier)}
            </div>
            <div style={{ textAlign: 'left' }}>
              <button
                onClick={() => navigate(`/profile/${offerData.responderId}`)}
                style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1f2937', background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit' }}
                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
              >
                {sellerUserData.username}
              </button>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                <span>{getMembershipTierText(sellerUserData.membershipTier)}</span>
                <span>·</span>
                <span style={{ color: '#f59e0b' }}>★</span>
                <span style={{ fontWeight: '600', color: '#374151' }}>{sellerUserData.averageRating > 0 ? sellerUserData.averageRating.toFixed(1) : '—'}</span>
                {sellerUserData.badge !== null && sellerUserData.badge !== undefined && (
                  <span>({sellerUserData.badge})</span>
                )}
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '10px' }}>
            {targetListingTo ? (
              <Link to={targetListingTo} aria-label={`View listing: ${targetPedal.make} ${targetPedal.model}`}
                style={{ display: 'block', lineHeight: 0, borderRadius: '8px', overflow: 'hidden' }}>
                {targetPhoto}
              </Link>
            ) : targetPhoto}
          </div>
          <div style={{ fontWeight: '700', fontSize: '1rem', marginBottom: '2px' }}>
            <span style={{ color: '#1f2937' }}>{targetPedal.make}</span>{' '}
            <span style={{ color: '#6b7280' }}>{targetPedal.model}</span>
          </div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '4px' }}>Condition: {targetPedal.condition}</div>
          <div style={{ fontWeight: '700', fontSize: '1.25rem', color: '#1f2937', marginBottom: '6px' }}>${targetPedal.price}</div>
          {targetListingTo && (
            <Link to={targetListingTo} style={{ color: '#5A9ECB', fontSize: '0.85rem', textDecoration: 'none' }}>
              View listing →
            </Link>
          )}
        </div>

      </div>

      {/* Lower Sections */}
      <div className="mb-4 purchase-offer-lower-sections" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
        {/* Photo gate: both parties, whatever the turn. */}
        {isOfferActive && (
          <PhotoGatePanel offer={offerData} collection="purchaseOffers" offerId={offerId} currentUserId={currentUserId} returnTo={`/purchase-offer-details/${offerId}`} onCancelOffer={mustWait ? cancelOffer : null} cancelling={cancellingOffer} />
        )}

        {/* Purchase History */}
        {offerData.history && offerData.history.length > 0 && (
          <div className="mb-4 border rounded p-3 bg-blue-50" style={{ width: '100%', margin: '0 auto 1rem auto' }}>
            <h4 className="font-semibold mb-2">Purchase History</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {offerData.history.map((entry, index, arr) => {
                let timestamp = 'Unknown time';
                let entryDate = null;
                if (entry.timestamp?.toDate) {
                  entryDate = entry.timestamp.toDate();
                } else if (entry.timestamp) {
                  const d = new Date(entry.timestamp);
                  if (!isNaN(d.getTime())) entryDate = d;
                }
                if (entryDate) {
                  timestamp = formatRelativeTime(entryDate);
                }

                let timeSeparator = null;
                if (index > 0) {
                  const prevEntry = arr[index - 1];
                  let prevDate = null;
                  if (prevEntry.timestamp?.toDate) prevDate = prevEntry.timestamp.toDate();
                  else if (prevEntry.timestamp) { const d = new Date(prevEntry.timestamp); if (!isNaN(d.getTime())) prevDate = d; }

                  if (prevDate && entryDate) {
                    const diffMs = Math.abs(entryDate.getTime() - prevDate.getTime());
                    const diffMins = Math.floor(diffMs / 60000);
                    const diffHrs = Math.floor(diffMins / 60);
                    const diffDays = Math.floor(diffHrs / 24);
                    let timeLabel;
                    if (diffDays > 0) timeLabel = `${diffDays} day${diffDays > 1 ? 's' : ''} later`;
                    else if (diffHrs > 0) timeLabel = `${diffHrs} hr${diffHrs > 1 ? 's' : ''} later`;
                    else if (diffMins > 0) timeLabel = `${diffMins} min later`;
                    else timeLabel = 'moments later';

                    timeSeparator = (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '6px 0' }}>
                        <div style={{ flex: 1, height: '1px', backgroundColor: '#d1d5db' }} />
                        <span style={{ fontSize: '0.75rem', color: '#9ca3af', whiteSpace: 'nowrap', fontStyle: 'italic' }}>{timeLabel}</span>
                        <div style={{ flex: 1, height: '1px', backgroundColor: '#d1d5db' }} />
                      </div>
                    );
                  }
                }

                const isMine = entry.from === currentUserId;
                const username = (entry.from === offerData.initiatorId) ? buyerUserData.username : sellerUserData.username;
                const senderUserData = entry.type === 'auto_decline'
                  ? { username: 'PedalxChange', photoURL: '/logo192.png' }
                  : (entry.from === offerData.initiatorId) ? buyerUserData : sellerUserData;
                const senderInitials = (senderUserData.username || '??').slice(0, 2).toUpperCase();
                const actionText = entry.type === 'offer' ? 'sent an offer' : entry.type === 'note' ? 'sent a comment' : entry.type === 'auto_decline' ? 'closed this offer automatically' : entry.responseType === 'cancel' ? 'cancelled the offer' : 'responded';

                const label = entry.type === 'offer' ? 'Initial Offer' : entry.type === 'note' ? 'Comment' : entry.type === 'auto_decline' ? 'Auto-Declined' : entry.responseType === 'cancel' ? 'Cancelled' : 'Response';

                return (
                  <React.Fragment key={index}>
                    {timeSeparator}
                    <div
                      className={`history-entry-card ${isMine ? 'history-entry-mine' : 'history-entry-theirs'}`}
                      style={{
                        backgroundColor: isMine ? '#f0f7fc' : '#fef9ee',
                        border: isMine ? '1.5px solid #5A9ECB' : '1.5px solid #d4a843',
                        borderRadius: '12px',
                        padding: '16px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
                        width: '100%'
                      }}
                    >
                      <div className="history-entry-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, flex: '1 1 auto' }}>
                          <div style={{
                            width: '28px', height: '28px', borderRadius: '50%', overflow: 'hidden',
                            backgroundColor: isMine ? '#dbeafe' : '#fef3c7',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.7rem', fontWeight: '700', color: isMine ? '#1d4ed8' : '#92400e', flexShrink: 0
                          }}>
                            {senderUserData.photoURL ? (
                              <img src={senderUserData.photoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : senderInitials}
                          </div>
                          {/* Wraps, same fix as OfferPage: comment bubbles are narrower than
                              offer bubbles and the name ran under the badge and date on mobile. */}
                          <span className="history-entry-username" style={{ fontSize: '0.85rem', color: '#374151', minWidth: 0, overflowWrap: 'anywhere', lineHeight: 1.25, textAlign: 'left' }}>
                            <strong>{senderUserData.username}</strong> {actionText}
                          </span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, marginLeft: '8px', gap: '2px' }}>
                          <span className="history-entry-badge" style={{
                            fontSize: '0.75rem', backgroundColor: '#f3f4f6', color: '#6b7280',
                            padding: '2px 8px', borderRadius: '9999px', fontWeight: '500', whiteSpace: 'nowrap'
                          }}>{label}</span>
                          <span className="history-entry-date" style={{ fontSize: '0.75rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>{timestamp}</span>
                        </div>
                      </div>

                      {entry.message && (
                        <p style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '4px', textAlign: 'left', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                          {entry.type === 'auto_decline' ? autoDeclineReasonText(offerData) : entry.message}
                        </p>
                      )}

                      {/* Attached images */}
                      {safeCommentPhotoUrls(entry.imageUrls).length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                          {safeCommentPhotoUrls(entry.imageUrls).map((url, idx) => (
                            <a key={idx} href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', lineHeight: 0 }}>
                              <img src={url} alt={`Attachment ${idx + 1}`} style={{ maxWidth: '180px', maxHeight: '180px', width: 'auto', height: 'auto', borderRadius: '8px', objectFit: 'cover', border: '1px solid #e5e7eb' }} />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Send a comment section */}
        {isOfferActive && (
          <div style={{ width: '100%', margin: '0 auto' }}>
            <h4 className="font-semibold" style={{ marginTop: 0, marginBottom: '2px', textAlign: 'left' }}>Send a comment</h4>
            <p className="comment-subtitle" style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: 0, marginBottom: '6px', textAlign: 'left' }}>Ask questions or share details without making a formal response.</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              multiple
              onChange={handleCommentFileSelect}
              style={{ display: 'none' }}
            />
            <div className="purchase-offer-textarea-button-container" style={{ width: '100%' }}>
              {pendingPreviews.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                  {pendingPreviews.map((preview, idx) => (
                    <div key={idx} style={{ position: 'relative' }}>
                      <img src={preview} alt={`Preview ${idx + 1}`} style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #e0e0e0' }} />
                      <button
                        type="button"
                        onClick={() => removeCommentImage(idx)}
                        aria-label="Remove image"
                        style={{ position: 'absolute', top: '-6px', right: '-6px', width: '22px', height: '22px', borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.95)', color: '#e74c3c', fontSize: '14px', lineHeight: 1, cursor: 'pointer', padding: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
              <AutoGrowTextarea
                placeholder="Type a comment or question..."
                maxLength={500}
                className="purchase-offer-note-textarea"
                style={{ marginBottom: '8px', padding: '12px', borderRadius: '8px', lineHeight: '1.5', display: 'block', width: '100%', border: '1px solid #e5e7eb', fontFamily: 'Nunito, sans-serif', color: '#374151', background: '#fff' }}
                rows={3}
                value={noteText}
                onChange={(e) => { setNoteText(e.target.value); setCommentError(''); }}
              />
              {commentError && (
                <p style={{ color: '#dc2626', fontSize: '0.85rem', margin: '0 0 8px', textAlign: 'left' }}>{commentError}</p>
              )}
              {!commentError && !commentCooldown.allowed && (
                <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: '0 0 8px', textAlign: 'left' }}>{cooldownMessage(commentCooldown.remainingMs)}</p>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px' }}>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sendingNote || pendingImageFiles.length >= MAX_COMMENT_IMAGES}
                  aria-label="Add photo"
                  style={{
                    flexShrink: 0, width: '46px', height: '46px', borderRadius: '50%',
                    border: '2px solid #e0e0e0', background: '#fff',
                    cursor: (sendingNote || pendingImageFiles.length >= MAX_COMMENT_IMAGES) ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                    opacity: (sendingNote || pendingImageFiles.length >= MAX_COMMENT_IMAGES) ? 0.4 : 1
                  }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                </button>
                <button
                  onClick={handleSendNote}
                  disabled={sendingNote || !commentCooldown.allowed || (!noteText.trim() && !pendingImageFiles.length)}
                  style={{
                    backgroundColor: (sendingNote || !commentCooldown.allowed || (!noteText.trim() && !pendingImageFiles.length)) ? '#d1d5db' : '#C89F3C',
                    color: 'white',
                    border: 'none',
                    borderRadius: '25px',
                    padding: '11px 28px',
                    fontFamily: 'Nunito, sans-serif',
                    fontWeight: '600',
                    fontSize: '15px',
                    cursor: (sendingNote || !commentCooldown.allowed || (!noteText.trim() && !pendingImageFiles.length)) ? 'not-allowed' : 'pointer',
                    opacity: (sendingNote || !commentCooldown.allowed || (!noteText.trim() && !pendingImageFiles.length)) ? 0.6 : 1
                  }}
                >
                  {sendingNote ? 'Sending...' : !commentCooldown.allowed ? `Wait ${commentCooldown.remainingSeconds}s` : 'Send comment'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Separator */}
        {isOfferActive && canRespond && (
          <div style={{ borderBottom: '1px solid #d9dce0', margin: '1.5rem 0 1.5rem 0' }} />
        )}

        {/* Response Options */}
        {canRespond && (
          <div className="purchase-offer-respond-container" style={{ width: '100%', margin: '0 auto' }}>
            {!isBuyer && sellingDisabled && (
              <SellingDisabledBanner mode="inline" />
            )}
            <div style={{ marginBottom: '0.5rem' }}>
              <h4 className="font-semibold" style={{ marginTop: 0, marginBottom: '4px', textAlign: 'left' }}>Respond to offer</h4>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: 0, marginBottom: '12px', textAlign: 'left' }}>Choose how you'd like to respond.</p>
              <div className="respond-options-list" style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '12px' }}>
                {[
                  { value: 'accept', label: 'Accept', desc: 'Complete the purchase' },
                  { value: 'decline', label: 'Not for me, thanks', desc: 'Politely pass' },
                  { value: 'counter', label: 'Counter offer', desc: 'Propose a different price' }
                ].map(opt => {
                  const photosPending = opt.value === 'accept' && isPhotoGateClosed(offerData);
                  const isOptionBlocked = (!isBuyer && sellingDisabled && (opt.value === 'accept' || opt.value === 'counter')) || photosPending;
                  return (
                    <label
                      key={opt.value}
                      onClick={() => { if (!isOptionBlocked) setResponseType(opt.value); }}
                      title={photosPending ? 'Photos of the pedal must be added and approved first' : (isOptionBlocked ? 'Selling, trading & listing are paused, pay outstanding invoice to restore' : undefined)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '12px 16px', borderRadius: '10px',
                        cursor: isOptionBlocked ? 'not-allowed' : 'pointer',
                        opacity: isOptionBlocked ? 0.5 : 1,
                        border: responseType === opt.value ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                        backgroundColor: responseType === opt.value ? '#eff6ff' : '#f9fafb',
                        transition: 'all 0.15s'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input
                          type="radio"
                          value={opt.value}
                          checked={responseType === opt.value}
                          onChange={(e) => { if (!isOptionBlocked) setResponseType(e.target.value); }}
                          disabled={isOptionBlocked}
                          style={{ flexShrink: 0, accentColor: '#3b82f6' }}
                        />
                        <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#111827' }}>{opt.label}</span>
                      </div>
                      <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                        {photosPending ? 'Photos pending' : (isOptionBlocked ? 'Selling paused' : opt.desc)}
                      </span>
                    </label>
                  );
                })}
              </div>

              {/* Counter Amount Input */}
              {responseType === 'counter' && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <label style={{ fontWeight: '500', fontSize: '0.85rem', color: '#374151', whiteSpace: 'nowrap' }}>Amount ($)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="0"
                      className="border border-gray-300 focus:border-blue-500 focus:outline-none"
                      style={{ flex: 1, padding: '8px 12px', fontSize: '0.95rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                      value={counterAmount}
                      onChange={(e) => setCounterAmount(formatCurrencyInput(e.target.value))}
                    />
                  </div>
                </div>
              )}

              {/* Cost Breakdown for Accept or Counter */}
              {(responseType === 'accept' || (responseType === 'counter' && counterAmount)) && (() => {
                const offerAmount = responseType === 'accept'
                  ? (offerData?.counterAmount || offerData?.offerAmount || 0)
                  : Number(counterAmount || 0);
                if (offerAmount <= 0 && responseType === 'counter') return null;

                const platformFee = getTradeCompletionFee();
                const cashFeePercentage = getCashFeePercentage();
                const cashFeePercDecimal = cashFeePercentage / 100;
                const cashFee = isBuyer ? Number((offerAmount * cashFeePercDecimal).toFixed(2)) : 0;
                const sellerTotal = offerAmount - platformFee;
                const buyerTotal = offerAmount + cashFee + (isBuyer ? salesTaxAmount : 0);

                return (
                  <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                    {/* Value summary */}
                    <div style={{ padding: '12px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', gap: '8px', fontSize: '0.85rem', color: '#1e3a5f' }}>
                        <div style={{ flex: 1, borderRight: '1px solid #bfdbfe', paddingRight: '8px' }}>
                          <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#64748b', letterSpacing: '0.05em', marginBottom: '4px' }}>
                            {isBuyer ? "YOU'RE PAYING" : "YOU'RE SELLING"}
                          </div>
                          {isBuyer ? (
                            <div>Cash: <strong>${Number(offerAmount).toFixed(2)}</strong></div>
                          ) : (
                            <div>{targetPedal.make} {targetPedal.model}: <strong>${Number(targetPedal.price).toFixed(2)}</strong></div>
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#64748b', letterSpacing: '0.05em', marginBottom: '4px' }}>
                            {isBuyer ? "YOU'RE GETTING" : "YOU'RE RECEIVING"}
                          </div>
                          {isBuyer ? (
                            <div>{targetPedal.make} {targetPedal.model}: <strong>${Number(targetPedal.price).toFixed(2)}</strong></div>
                          ) : (
                            <div>Cash: <strong>${Number(offerAmount).toFixed(2)}</strong></div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Cost breakdown */}
                    <div style={{ borderBottom: '1px solid #e5e7eb', marginBottom: '12px' }} />
                    <h5 style={{ fontWeight: '600', fontSize: '0.95rem', color: '#111827', marginTop: 0, marginBottom: '10px' }}>Your cost breakdown</h5>
                    <div style={{ fontSize: '0.85rem', color: '#374151' }}>
                      {isBuyer ? (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                            <span>Purchase amount</span>
                            <span>${offerAmount.toFixed(2)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', color: '#6b7280' }}>
                            <span>Cash processing fee ({cashFeePercentage}%)</span>
                            <span>${cashFee.toFixed(2)}</span>
                          </div>
                          {firestoreUser?.shippingAddress && (
                            <>
                              {calculatingSalesTax && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', color: '#6b7280' }}>
                                  <span>Sales Tax</span>
                                  <span>Calculating...</span>
                                </div>
                              )}
                              {!calculatingSalesTax && salesTaxAmount > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                  <span>{salesTaxState} Sales Tax ({salesTaxRate}%)</span>
                                  <span>${salesTaxAmount.toFixed(2)}</span>
                                </div>
                              )}
                            </>
                          )}
                          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '0.95rem' }}>
                            <span>Total you'll pay</span>
                            <span style={{ color: '#111827' }}>${buyerTotal.toFixed(2)}</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                            <span>Sale price</span>
                            <span>${offerAmount.toFixed(2)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', color: '#6b7280' }}>
                            <span>Platform fee</span>
                            <span>-${platformFee.toFixed(2)}</span>
                          </div>
                          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '0.95rem' }}>
                            <span>{sellerTotal >= 0 ? "Total you'll receive" : "Total you'll owe"}</span>
                            <span style={{ color: sellerTotal > 0 ? '#15803d' : '#111827' }}>${Math.abs(sellerTotal).toFixed(2)}</span>
                          </div>
                        </>
                      )}
                    </div>
                    {membershipTier === 'free' && (
                      <div style={{ marginTop: '16px', marginBottom: '16px', fontSize: '0.75rem', color: '#6b7280', textAlign: 'center', backgroundColor: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px', padding: '8px' }}>
                        <strong>Click <a href="/profile" style={{ color: '#5A9ECB' }} className="hover:opacity-80 underline">here</a> to become a paid member</strong> and receive reduced platform fees!
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {responseType === 'decline' && (
              <div style={{ marginBottom: '16px', textAlign: 'left' }}>
                <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0 0 8px' }}>
                  Let them know why (optional).
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {DECLINE_REASONS.map(reason => {
                    const isSelected = declineReason === reason.label;
                    return (
                      <button
                        key={reason.value}
                        type="button"
                        onClick={() => {
                          if (reason.routesToCounter) {
                            setDeclineReason('');
                            setResponseType('counter');
                            return;
                          }
                          setDeclineReason(isSelected ? '' : reason.label);
                        }}
                        style={{
                          padding: '8px 14px',
                          borderRadius: '9999px',
                          border: isSelected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                          backgroundColor: isSelected ? '#eff6ff' : '#f9fafb',
                          color: '#374151',
                          fontFamily: 'Nunito, sans-serif',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.15s'
                        }}
                      >
                        {reason.label}
                      </button>
                    );
                  })}
                </div>
                <p style={{ fontSize: '0.78rem', color: '#9ca3af', margin: '10px 0 0' }}>
                  A "not this time" is normal. It helps everyone find a better fit.
                </p>
              </div>
            )}

            <div className="purchase-offer-textarea-button-container" style={{ width: '100%' }}>
              <div style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '12px', marginBottom: '16px' }}>
                <AutoGrowTextarea
                  placeholder="Add a short note..."
                  maxLength={500}
                  className="purchase-offer-note-textarea"
                  style={{ width: '100%', padding: '0', lineHeight: '1.5', display: 'block', border: 'none', background: 'transparent', outline: 'none', fontFamily: 'Nunito, sans-serif', color: '#374151' }}
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>

              {responseType === 'accept' && !isAddressComplete(firestoreUser?.shippingAddress) && (
                <p style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: '0.75rem', lineHeight: '1.4', textAlign: 'center' }}>
                  A shipping address is required before accepting this offer.{' '}
                  <button
                    type="button"
                    onClick={() => setShowAddressModal(true)}
                    style={{ color: '#dc2626', textDecoration: 'underline', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 'inherit', fontFamily: 'inherit' }}
                  >
                    Add address
                  </button>
                </p>
              )}

              <div className="purchase-offer-response-buttons" style={{ display: 'flex', gap: '10px', maxWidth: '400px', marginLeft: 'auto' }}>
                <button
                  onClick={() => { setResponseType(''); setNote(''); setDeclineReason(''); setCounterAmount(''); }}
                  style={{
                    backgroundColor: '#E5E7EB',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '9999px',
                    padding: '12px 20px',
                    fontFamily: 'Nunito, sans-serif',
                    fontWeight: '600',
                    fontSize: '15px',
                    cursor: 'pointer',
                    flex: '1 1 0'
                  }}
                  className="hover:opacity-80 transition-opacity"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResponse}
                  style={{
                    backgroundColor: (loading || !responseType || (responseType === 'counter' && !counterAmount) || (responseType === 'accept' && !isAddressComplete(firestoreUser?.shippingAddress))) ? '#9CA3AF' : '#C9A227',
                    color: 'white',
                    border: 'none',
                    borderRadius: '9999px',
                    padding: '12px 20px',
                    fontFamily: 'Nunito, sans-serif',
                    fontWeight: '700',
                    fontSize: '15px',
                    cursor: (loading || !responseType || (responseType === 'accept' && !isAddressComplete(firestoreUser?.shippingAddress))) ? 'not-allowed' : 'pointer',
                    opacity: (loading || !responseType || (responseType === 'accept' && !isAddressComplete(firestoreUser?.shippingAddress))) ? 0.6 : 1,
                    flex: '2 1 0'
                  }}
                  disabled={loading || !responseType || (responseType === 'counter' && !counterAmount) || (responseType === 'accept' && !isAddressComplete(firestoreUser?.shippingAddress))}
                  className="hover:opacity-90 transition-opacity"
                >
                  {loading ? 'Submitting...' : 'Submit response'}
                </button>
              </div>
            </div>

            {submitPhase === 'syncing' && (
              <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem', color: '#4A5759', marginTop: '0.5rem' }}>
                Response queued, syncing...
              </p>
            )}
            {errorMsg && <p className="text-red-600 mt-2">{errorMsg}</p>}
          </div>
        )}

        {mustWait && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded" style={{ maxWidth: '600px', margin: '1rem auto 0 auto' }}>
            <p className="text-blue-800">
              <strong>Status:</strong> {offerData.status === 'pending' ? 'Waiting for response' : 'Counter offer sent'}
            </p>
            <p className="text-sm text-blue-600 mt-1">
              {offerData.status === 'pending' 
                ? 'The recipient will respond to your offer.'
                : 'The other party will respond to your counter offer.'
              }
            </p>
            <button
              type="button"
              disabled={cancellingOffer}
              onClick={cancelOffer}
              style={{ marginTop: '10px', background: 'none', border: '1px solid #9CA3AF', borderRadius: '25px', padding: '6px 14px', fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.85rem', color: '#4B5563', cursor: cancellingOffer ? 'wait' : 'pointer' }}
            >
              {cancellingOffer ? 'Cancelling…' : 'Cancel offer'}
            </button>
          </div>
        )}

        {/* Show accepted status */}
        {offerData.status === 'cancelled' && (
          <div className="mt-4 p-3 rounded" style={{ maxWidth: '600px', margin: '1rem auto', background: '#F3F4F6', border: '1px solid #D1D5DB' }}>
            <p style={{ color: '#374151' }}>
              <strong>Status:</strong> Offer cancelled
            </p>
            <p className="text-sm mt-1" style={{ color: '#6B7280' }}>
              {offerData.cancelledBy === currentUserId ? 'You cancelled this offer.' : 'The other member cancelled this offer.'} It can no longer be accepted.
            </p>
          </div>
        )}
        {offerData.status === 'accepted' && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded" style={{ maxWidth: '600px', margin: '1rem auto 0 auto' }}>
            <p className="text-green-800">
              <strong>Status:</strong> Purchase Accepted!
            </p>
            <p className="text-sm text-green-600 mt-1">
              This purchase has been accepted. Complete your payment to proceed.
            </p>
          </div>
        )}

        {/* Show expired status */}
        {offerData.status === 'expired' && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded" style={{ maxWidth: '600px', margin: '1rem auto 0 auto' }}>
            <p className="text-yellow-800">
              <strong>Status:</strong> Offer Expired
            </p>
            <p className="text-sm text-yellow-700 mt-1">
              This purchase offer was not responded to within 48 hours and has expired.
            </p>
          </div>
        )}

        {/* Show declined status */}
        {offerData.status === 'declined' && isAutoDeclined(offerData) && (
          <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded" style={{ maxWidth: '600px', margin: '1rem auto 0 auto', textAlign: 'center' }}>
            <p className="text-gray-800">
              <strong>Status:</strong> Closed automatically
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {autoDeclineReasonText(offerData)}
            </p>
          </div>
        )}
        {offerData.status === 'declined' && !isAutoDeclined(offerData) && (
          <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded" style={{ maxWidth: '600px', margin: '1rem auto 0 auto', textAlign: 'center' }}>
            <p className="text-gray-800">
              <strong>Status:</strong> Not a fit this time
            </p>
            <p className="text-sm text-gray-600 mt-1">
              This one didn't work out. That's a normal part of trading.
            </p>
            {/* Only the buyer needs somewhere to go next; the seller is already done. */}
            {offerData.initiatorId === currentUserId && (
              <a
                href="/latest-pedals"
                style={{
                  display: 'inline-block', marginTop: '12px', backgroundColor: '#C9A227',
                  color: 'white', padding: '10px 22px', borderRadius: '9999px',
                  fontWeight: '700', fontSize: '0.9rem', textDecoration: 'none',
                  fontFamily: 'Nunito, sans-serif'
                }}
              >
                Browse more pedals
              </a>
            )}
          </div>
        )}

      </div>

      {/* Fixed gap below content */}
      <div style={{ height: '60px' }}></div>
      
      {/* Footer */}
      <div className="bg-gray-100 border-t border-gray-200 py-4 purchase-offer-page-footer" style={{ paddingBottom: '10px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Need help or found a bug?{' '}
              <button 
                onClick={() => openContact({ category: 'support' })}
                className="hover:underline"
                style={{ 
                  color: '#5A9ECB',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer'
                }}
              >
                Contact PXC Support
              </button>
            </p>
        </div>
      </div>
      </div>
    </div>

      {/* Inline shipping-address modal: opens only when the user clicks
          Accept without a complete address. */}
      <AddressRequiredModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        onSaved={() => setShowAddressModal(false)}
        user={firestoreUser}
        context="purchase-accept"
      />

    </>
  );
};

export default PurchaseOfferDetails;
